import React, { ReactNode, useState, useEffect } from 'react'
import logger from '@adc/logger'
// @ts-ignore no types
import ms from 'ms'
import * as graph from '@adc/sdk'
import type { Maybe } from '@adc/sdk/types'
// @ts-ignore no types
import { hooks } from '@adc/ui-adc'
import _cx from 'classnames/bind'
import { useListingContext } from 'common/context/listing'
import * as CostCalculations from 'common/modules/bidBanner/costCalculations'
import ContractButton from 'common/modules/bidBanner/contractButton'
import {
  WinningBid,
  BuyersPremiumAndTotal,
} from 'common/modules/bidBanner/bidForm/costBreakdown'
import EarnestMoneyDeposit from 'common/modules/bidBanner/bidForm/earnestMoneyDeposit'
import * as BidDetails from 'common/modules/bidBanner/bidDetails'
import Header from 'common/modules/bidBanner/header'
import {
  Container,
  LeftMarginSection,
} from 'common/modules/bidBanner/container'
import { SimilarPropertiesButton } from 'common/modules/similarProperties'
import { getServerTimeMoment } from 'common/modules/auctionCountdown'
import {
  MatchWinningBidButton,
  MatchBidWinnerInfo,
  MatchBidAvailableInfo,
} from '../matchBid'
import TimeInformation from '../timeInformation'
import CostBreakdown from '../costBreakdown'
import {
  LostReserveNotMetTooltip,
  SubjectToApprovalTooltip,
  SubjectToConfirmationTooltip,
} from '../tooltips'
import { PreviousBid } from '../../../modules/bidBanner/bidForm/costBreakdown/index'
import WinnerNextSteps from '../winnerNextSteps'
import hasSimilarProperties from '../../../utils/hasSimilarProperties'
import MatchBidTimer from '../matchBid/matchBidTimer'
// @ts-ignore no types
import { BidBannerProps } from '../index'
import ReserveMetRibbon from '../reserveRibbon'
import cls from './styles.scss'
import { BID_BANNER_STATES, getBidBannerState } from './common'
const cx = _cx.bind(cls)

const HEADER_WON = 'Congratulations! You won the auction.'
const HEADER_MATCH_BID_AVAILABLE = 'This Auction is Finalizing'
const HEADER_MATCH_BID_SUBMITTED = 'You have successfully Matched The Bid'
const HEADER_TEXTS = {
  NO_BIDS_MET_RESERVE: "The auction ended, but Reserve wasn't met.",
  AUCTION_ENDED: 'The auction ended.',
  USER_LOST_RESERVE_MET: 'Property sold. You are not the winning bidder.',
  USER_WON_SUBJECT_TO_APPROVAL: HEADER_WON,
  USER_WON_OUTRIGHT: HEADER_WON,
  USER_WON_SUBJECT_TO_CONFIRMATION: HEADER_WON,
  USER_WON_MANUALLY: 'Congratulations! You have the winning offer.',
  USER_CAN_SUBMIT_FINAL_OFFER:
    'For a limited time, only you can make an offer on this property.',
  USER_CAN_SUBMIT_FINAL_OFFER_SUBTEXT:
    'Your bid did not meet the reserve. Increase your chances of winning by making a more competitive offer with justifications.',
  FINAL_OFFER_PENDING: 'Your offer was submitted.',
  FINAL_OFFER_PENDING_SUBTEXT: 'Seller has 5 days to respond.',
  MATCH_BID_AVAILABLE:
    'You were outbid - but you still have a chance to win the property if the winning bidder does not submit the Contracting Information Form in the 6-hour time frame.',
  MATCH_BID_SUBMITTED:
    'There is still a chance, if the winning bid does not go through, you will be awarded the property.',
}

const LISTING_UPDATE_FETCH_DELAY = ms('5s')

interface AfterProps extends BidBannerProps {
  bidDeposit: Maybe<number | undefined>
  similarProperties?: any[]
  upsertReoPurchaseProfile: any
  refreshCurrentListing: () => void
}

export const After = (props: AfterProps) => {
  const [haveSeenMatchBidSubmitted, setHaveSeenMatchBidSubmitted] =
    useState(false)

  const {
    bidDeposit,
    similarProperties,
    user,
    upsertReoPurchaseProfile,
    refreshCurrentListing,
    featureFlags,
  } = props

  const { segment, listing } = useListingContext()

  /**
   * NOTE: A better way to do this would be to store whether we had previously seen any bid banner _before_ the auction
   * ended. If we didn't then the server has likely given us the correct information before we initially rendered and
   * this is redundant. Since we don't track that now, the drawback to this will be a single extra request if a user
   * opens a PDP page where the segment has already ended.
   */
  hooks.useMountEffect(() => {
    setTimeout(() => {
      if (
        refreshCurrentListing &&
        typeof refreshCurrentListing === 'function'
      ) {
        refreshCurrentListing()
      } else {
        logger.error('refreshCurrentListing is not a function')
      }
    }, LISTING_UPDATE_FETCH_DELAY)

    const currentUserHighestBidType = segment?.current_user_highest_bid?.type

    if (
      currentUserHighestBidType ===
      graph.enums.OnlineAuctionSegmentBidType.Match
    )
      setHaveSeenMatchBidSubmitted(true)
  })

  useEffect(() => {
    const currentUserHighestBidType = segment?.current_user_highest_bid?.type

    if (
      currentUserHighestBidType ===
        graph.enums.OnlineAuctionSegmentBidType.Match &&
      !haveSeenMatchBidSubmitted
    ) {
      setHaveSeenMatchBidSubmitted(true)
    }
  }, [haveSeenMatchBidSubmitted, segment?.current_user_highest_bid?.type])

  const renderAllCosts = () => {
    const winningBidAmount = segment?.result?.winning_bid_amount

    let calculations = null
    if (winningBidAmount) {
      calculations = listing.__methods__.getCostCalculations(winningBidAmount)
    }

    if (!calculations) return null

    return (
      <>
        <div className={cx('u-mb-3', 'u-mt-3')}>
          <WinningBid winningBidAmount={winningBidAmount} />
          <BuyersPremiumAndTotal
            listing={listing}
            bidAmount={winningBidAmount}
          />
          <div className={cx('centered-section', 'u-mb-3')}>
            <EarnestMoneyDeposit
              listing={listing}
              earnestMoneyDeposit={calculations.earnestMoneyDeposit}
            />
          </div>
        </div>
        <div className={cx('u-mb-3')}>
          <ContractButton />
        </div>
        <div className={cx('centered-section')}>
          <CostBreakdown
            bidAmount={winningBidAmount}
            isBidAmountValid
            segment={segment}
            listing={listing}
          />
        </div>
      </>
    )
  }

  const renderWinningBidOnly = () => {
    const winningBidAmount = segment?.result?.winning_bid_amount
    return (
      <>
        <div className={cx('u-mb-3', 'u-mt-3')}>
          <CostCalculations.YourWinningBid
            value={winningBidAmount}
            valueWeight='heavy'
          />
        </div>
        <div className={cx('u-mb-3')}>
          <ContractButton />
        </div>
      </>
    )
  }

  const renderWinnerCosts = () => {
    return listing.listing_configuration?.is_buyers_premium_available ||
      listing.listing_configuration?.is_earnest_money_deposit_available
      ? renderAllCosts()
      : renderWinningBidOnly()
  }

  const renderRightRibbon = () => {
    if (!segment) return null
    return <ReserveMetRibbon segment={segment} />
  }

  const renderUserWonManuallySubjectToConfirmation = () => {
    return (
      <Container
        ribbonColor='success'
        left={
          <>
            <Header
              containerClassName={cx('u-mb-3', 'u-mt-2')}
              color='success'
              subText={<SubjectToConfirmationTooltip />}
              icon={<Header.IconGavel />}
              text={HEADER_TEXTS.USER_WON_MANUALLY}
            />
            <LeftMarginSection>
              <WinnerNextSteps />
            </LeftMarginSection>
          </>
        }
        right={renderWinningBidOnly()}
      />
    )
  }

  const renderUserWonSubjectToConfirmation = () => {
    return (
      <Container
        ribbonColor='success'
        left={
          <>
            <Header
              containerClassName={cx('u-mb-3', 'u-mt-2')}
              color='success'
              subText={<SubjectToConfirmationTooltip />}
              icon={<Header.IconGavel />}
              text={HEADER_TEXTS.USER_WON_SUBJECT_TO_CONFIRMATION}
            />
            <LeftMarginSection>
              <WinnerNextSteps containerClassName={cx('u-mb-3')} />
              <BidDetails.After
                segment={segment}
                listing={listing}
                bidDeposit={bidDeposit}
              />
            </LeftMarginSection>
          </>
        }
        right={renderWinnerCosts()}
        rightRibbon={renderRightRibbon()}
      />
    )
  }

  const renderUserWonManuallyOutright = () => {
    return (
      <Container
        ribbonColor='success'
        left={
          <>
            <Header
              containerClassName={cx('u-mb-3', 'u-mt-2')}
              color='success'
              icon={<Header.IconGavel />}
              text={HEADER_TEXTS.USER_WON_MANUALLY}
            />
            <LeftMarginSection>
              <WinnerNextSteps />
            </LeftMarginSection>
          </>
        }
        right={renderWinningBidOnly()}
      />
    )
  }

  const renderUserWonOutright = () => {
    return (
      <Container
        ribbonColor='success'
        left={
          <>
            <Header
              containerClassName={cx('u-mb-3', 'u-mt-2')}
              color='success'
              icon={<Header.IconGavel />}
              text={HEADER_TEXTS.USER_WON_OUTRIGHT}
            />
            <LeftMarginSection>
              <WinnerNextSteps containerClassName={cx('u-mb-3')} />
              <BidDetails.After segment={segment} listing={listing} />
            </LeftMarginSection>
          </>
        }
        right={renderWinnerCosts()}
        rightRibbon={renderRightRibbon()}
      />
    )
  }

  const renderUserWonSubjectToApproval = () => {
    return (
      <Container
        ribbonColor='success'
        left={
          <>
            <Header
              containerClassName={cx('u-mb-3', 'u-mt-2')}
              color='success'
              icon={<Header.IconGavel />}
              subText={<SubjectToApprovalTooltip />}
              text={HEADER_TEXTS.USER_WON_SUBJECT_TO_APPROVAL}
            />
            <LeftMarginSection>
              <WinnerNextSteps containerClassName={cx('u-mb-3')} />
              <BidDetails.After segment={segment} listing={listing} />
            </LeftMarginSection>
          </>
        }
        right={renderWinnerCosts()}
        rightRibbon={renderRightRibbon()}
      />
    )
  }

  const renderSimilarPropertiesButtonIfAvailable = () => {
    return hasSimilarProperties(similarProperties) ? (
      <div className='u-mt-3'>
        <SimilarPropertiesButton similarProperties={similarProperties} />
      </div>
    ) : null
  }

  const renderUserLost = (
    headerText: string,
    headerSubText: ReactNode = null
  ) => {
    return (
      <Container
        ribbonColor='gray'
        left={
          <>
            <div className={cx('u-mb-5')}>
              <TimeInformation segment={segment} />
            </div>
            <Header
              containerClassName={cx('u-mb-3')}
              color='gray'
              icon={<Header.IconGavel color='gray' />}
              subText={headerSubText}
              text={headerText}
            />
            <LeftMarginSection>
              <BidDetails.After segment={segment} listing={listing} />
            </LeftMarginSection>
          </>
        }
        right={renderSimilarPropertiesButtonIfAvailable()}
      />
    )
  }

  const renderLoserCostsForMatchBid = (isMatchBidSubmitted: boolean) => {
    if (!segment?.current_highest_bid?.bid_amount) {
      return null
    } else {
      const calculations = listing.__methods__.getCostCalculations(
        segment?.current_highest_bid.bid_amount
      )

      return (
        <>
          <div className={cx('u-mb-3', 'u-mt-3')}>
            {!isMatchBidSubmitted && (
              <PreviousBid
                previousBidAmount={segment.current_user_highest_bid?.bid_amount}
              />
            )}
            <WinningBid
              labelText={isMatchBidSubmitted ? 'Your Match Bid' : 'Winning Bid'}
              winningBidAmount={segment.current_highest_bid.bid_amount}
            />
            <BuyersPremiumAndTotal
              listing={listing}
              bidAmount={segment.current_highest_bid.bid_amount}
            />
          </div>
          <div className={cx('centered-section', 'u-mb-3')}>
            <EarnestMoneyDeposit
              listing={listing}
              earnestMoneyDeposit={calculations.earnestMoneyDeposit}
            />
          </div>
          <div className={cx('u-mb-3')}>
            <MatchWinningBidButton
              segment={segment}
              user={user}
              isMatchBidSubmitted={isMatchBidSubmitted}
              calculations={calculations}
              handleMatchBidSubmittedSuccess={handleMatchBidSubmittedSuccess}
              upsertReoPurchaseProfile={upsertReoPurchaseProfile}
            />
          </div>
        </>
      )
    }
  }

  const renderMatchBidUserWonHeader = (bidBannerState: string) => {
    switch (bidBannerState) {
      case BID_BANNER_STATES.USER_WON_SUBJECT_TO_APPROVAL:
        return (
          <Header
            containerClassName={cx('u-mb-3', 'u-mt-2')}
            color='success'
            icon={<Header.IconGavel />}
            subText={<SubjectToApprovalTooltip />}
            text={HEADER_TEXTS.USER_WON_SUBJECT_TO_APPROVAL}
          />
        )
      case BID_BANNER_STATES.USER_WON_SUBJECT_TO_CONFIRMATION:
        return (
          <Header
            containerClassName={cx('u-mb-3', 'u-mt-2')}
            color='success'
            subText={<SubjectToConfirmationTooltip />}
            icon={<Header.IconGavel />}
            text={HEADER_TEXTS.USER_WON_SUBJECT_TO_CONFIRMATION}
          />
        )
      case BID_BANNER_STATES.USER_WON_OUTRIGHT:
        return (
          <Header
            containerClassName={cx('u-mb-3', 'u-mt-2')}
            color='success'
            icon={<Header.IconGavel />}
            text={HEADER_TEXTS.USER_WON_OUTRIGHT}
          />
        )
      default:
        return null
    }
  }

  const renderMatchBidUserWon = (
    bidBannerState: string,
    hasReserveBeenMet: boolean
  ) => {
    const matchBidCount = segment?.match_bid_count

    const matchBiddingPeriodEndDate = segment?.match_bidding_period_end_date

    return (
      <Container
        ribbonColor='success'
        left={
          <>
            {renderMatchBidUserWonHeader(bidBannerState)}
            <LeftMarginSection>
              <WinnerNextSteps containerClassName={cx('u-mb-3')} />
              <MatchBidWinnerInfo />
              <BidDetails.MatchBidAfter
                matchBidCount={matchBidCount}
                hasReserveBeenMet={hasReserveBeenMet}
              />
            </LeftMarginSection>
          </>
        }
        right={
          <>
            {matchBiddingPeriodEndDate && (
              <MatchBidTimer
                matchBiddingPeriodEndDate={matchBiddingPeriodEndDate}
                text='Complete your Form within:'
                currentServerTime={getServerTimeMoment(segment)}
              />
            )}
            {renderWinnerCosts()}
          </>
        }
        rightRibbon={renderRightRibbon()}
      />
    )
  }

  const renderMatchBidUserLost = (
    isMatchBidSubmitted: boolean,
    hasReserveBeenMet: boolean
  ) => {
    const matchBiddingPeriodEndDate = segment?.match_bidding_period_end_date

    return (
      <Container
        ribbonColor='gray'
        left={
          <>
            <div className={cx('u-mb-5')}>
              <TimeInformation segment={segment} />
            </div>
            <Header
              containerClassName={cx('u-mb-3')}
              color='gray'
              icon={<Header.IconGavel color='gray' />}
              subText={
                isMatchBidSubmitted
                  ? HEADER_TEXTS.MATCH_BID_SUBMITTED
                  : HEADER_TEXTS.MATCH_BID_AVAILABLE
              }
              text={
                isMatchBidSubmitted
                  ? HEADER_MATCH_BID_SUBMITTED
                  : HEADER_MATCH_BID_AVAILABLE
              }
            />
            <LeftMarginSection>
              {!isMatchBidSubmitted && <MatchBidAvailableInfo />}
              <BidDetails.MatchBidAfter hasReserveBeenMet={hasReserveBeenMet} />
            </LeftMarginSection>
          </>
        }
        right={
          <>
            {matchBiddingPeriodEndDate && (
              <MatchBidTimer
                matchBiddingPeriodEndDate={matchBiddingPeriodEndDate}
                currentServerTime={getServerTimeMoment(segment)}
              />
            )}
            {renderLoserCostsForMatchBid(isMatchBidSubmitted)}
          </>
        }
        rightRibbon={renderRightRibbon()}
      />
    )
  }

  const handleMatchBidSubmittedSuccess = () =>
    setHaveSeenMatchBidSubmitted(true)

  // segment?.configuration?.is_match_bidding_enabled if this field is true
  // before the auction has ended it will stay true after the acion ends
  // this will cause match bid feature being shown to users in auctions where match bidding
  // was initially enabled but the auction ended without a winner. In this case match bidding
  // should not be shown even though is_match_bidding_enabled flag is true.
  // segment?.match_bidding_period_end_date feild stays null if auction ends with no winners
  // and listing status as No Sale. We could use that to prevent match bidding ui from being shown
  // in auctions that ended with no winner.
  const isMatchBiddingEnabled =
    segment?.configuration?.is_match_bidding_enabled &&
    segment?.match_bidding_period_end_date &&
    !segment?.is_match_bidding_ended &&
    featureFlags['fe.resi.enable-match-bidding']

  const hasReserveBeenMet =
    segment?.reserve_status === graph.enums.ReserveStatus.ReserveMet

  // TODO: look into modifying blueprint to solve the issue of current_user_highest_bid
  // this should only be shown to users who have placed a bid
  // not using current_user_highest_bid on online segment at the moment since this field is only
  // fetched by polling request and is absent on online_segment from websocket update
  // biddinStatus.currentUserBidAmount returns 0 for user who have placed no bids
  if (
    isMatchBiddingEnabled &&
    segment.current_user_highest_bid?.bid_amount &&
    segment.current_user_highest_bid?.bid_amount > 0
  ) {
    switch (getBidBannerState(segment, listing)) {
      case BID_BANNER_STATES.USER_WON_SUBJECT_TO_APPROVAL:
        return renderMatchBidUserWon(
          BID_BANNER_STATES.USER_WON_SUBJECT_TO_APPROVAL,
          hasReserveBeenMet
        )
      case BID_BANNER_STATES.USER_WON_SUBJECT_TO_CONFIRMATION:
        return renderMatchBidUserWon(
          BID_BANNER_STATES.USER_WON_SUBJECT_TO_CONFIRMATION,
          hasReserveBeenMet
        )
      case BID_BANNER_STATES.USER_WON_OUTRIGHT:
        return renderMatchBidUserWon(
          BID_BANNER_STATES.USER_WON_OUTRIGHT,
          hasReserveBeenMet
        )

      case BID_BANNER_STATES.USER_LOST_RESERVE_MET:
      case BID_BANNER_STATES.USER_LOST_RESERVE_NOT_MET:
        return renderMatchBidUserLost(
          haveSeenMatchBidSubmitted,
          hasReserveBeenMet
        )

      default:
        return null
    }
  }

  switch (getBidBannerState(segment, listing)) {
    case BID_BANNER_STATES.USER_WON_MANUALLY_SUBJECT_TO_CONFIRMATION:
      return renderUserWonManuallySubjectToConfirmation()
    case BID_BANNER_STATES.USER_WON_MANUALLY_OUTRIGHT:
      return renderUserWonManuallyOutright()
    case BID_BANNER_STATES.USER_WON_SUBJECT_TO_APPROVAL:
      return renderUserWonSubjectToApproval()
    case BID_BANNER_STATES.USER_WON_SUBJECT_TO_CONFIRMATION:
      return renderUserWonSubjectToConfirmation()
    case BID_BANNER_STATES.USER_WON_OUTRIGHT:
      return renderUserWonOutright()
    case BID_BANNER_STATES.USER_LOST_RESERVE_NOT_MET:
      return renderUserLost(
        HEADER_TEXTS.NO_BIDS_MET_RESERVE,
        <LostReserveNotMetTooltip />
      )
    case BID_BANNER_STATES.USER_LOST_RESERVE_MET:
      return renderUserLost(
        HEADER_TEXTS.AUCTION_ENDED,
        HEADER_TEXTS.USER_LOST_RESERVE_MET
      )
    default:
      return null
  }
}
